import { Button } from "primereact/button";
import { useEffect, useMemo, useRef, useState } from "react";

const normalizeTopikData = (raw) => {
  if (!raw || typeof raw !== "object") {
    return {
      meta: {},
      passage: "",
      questionParts: [],
      choices: [],
      correctId: null,
      rationale: "",
      distractorAnalysis: [],
      cacheKey: "",
    };
  }

  const passage = raw.passage || "";
  const questionText = raw.question_text || raw.question || "";
  const explanation =
    raw.explanation || raw.answer?.explanation || raw?.answer_explanation || {};

  const splitParts = questionText
    ? questionText
        .split(".")
        .map((part) => part.trim())
        .filter(Boolean)
    : [];

  return {
    meta: raw.metadata || {},
    passage,
    questionParts: splitParts,
    choices: Array.isArray(raw.choices) ? raw.choices : [],
    correctId:
      raw.correct_answer_id ??
      raw.answer?.id ??
      raw.answer_id ??
      raw?.answer?.answer_id ??
      null,
    rationale: explanation?.rationale,
    distractorAnalysis: Array.isArray(explanation?.distractor_analysis)
      ? explanation.distractor_analysis
      : [],
    cacheKey: raw.id || raw.session_id || raw.features_id || JSON.stringify(raw),
  };
};

const TopikInteractiveCard = ({
  data,
  languageText,
  className = "topik-interactive-card",
}) => {
  const normalized = useMemo(() => normalizeTopikData(data), [data]);
  const [selectedId, setSelectedId] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const groupNameRef = useRef(
    `topik-choice-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => {
    setSelectedId(null);
    setShowResult(false);
    setIsLocked(false);
  }, [normalized.cacheKey]);

  return (
    <div className={className}>
      {normalized.passage && (
        <>
          <div className="mt-5 mb-3 main-question">{languageText?.passage}</div>
          <div
            className="passage"
            dangerouslySetInnerHTML={{ __html: normalized.passage }}
          />
        </>
      )}

      {normalized.questionParts.length > 0 && (
        <div className="flex flex-column gap-2">
          {normalized.questionParts.map((part, index) => (
            <div
              className={`${index === 0 ? "main-question" : ""}`}
              key={`${part}-${index}`}
              dangerouslySetInnerHTML={{ __html: part }}
            />
          ))}
        </div>
      )}

      <div className="mt-3 text-default-txt font-bold">
        {languageText?.required}
      </div>

      <div className="questions-list">
        {normalized.choices.map((choice) => {
          const isCorrectChoice = choice.id === normalized.correctId;
          const isSelected = selectedId === choice.id;
          const isWrongSelected = showResult && isSelected && !isCorrectChoice;
          const optionClass = `question-option ${
            isCorrectChoice && showResult ? "correct" : ""
          }${isWrongSelected ? "incorrect" : ""} ${
            isSelected ? "selected" : ""
          }`;

          return (
            <label
              key={choice.id}
              className={optionClass}
              style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
            >
              <input
                type="radio"
                name={showResult ? undefined : groupNameRef.current}
                checked={isSelected || (showResult && isCorrectChoice)}
                className="w-1rem h-1rem"
                readOnly={isLocked}
                onClick={(event) => {
                  if (isLocked) {
                    event.preventDefault();
                  }
                }}
                onChange={() => {
                  if (isLocked) return;
                  setSelectedId(choice.id);
                }}
                style={{ cursor: isLocked ? "not-allowed" : "pointer" }}
              />
              <span className="line-height-20">{choice.text}</span>
            </label>
          );
        })}
      </div>

      {!showResult ? (
        <div className="my-3">
          <Button
            label={languageText?.checkAnswer}
            className={`answer-button ${
              selectedId !== null && !showResult ? "active" : ""
            }`}
            onClick={() => {
              setShowResult(true);
              setIsLocked(true);
            }}
            disabled={selectedId === null}
          />
        </div>
      ) : (
        <div className="my-3">
          <div className="surface-200" style={{ height: 1 }} />
        </div>
      )}

      {showResult && (
        <div className="topik-section mt-3 mb-5">
          {normalized.rationale && (
            <div className="regular-text flex flex-column gap-2">
              <span className="flex align-items-center gap-2 correct">
                <i className="pi pi-check-circle text-green-500 text-base"></i>
                {languageText?.answerExplanation}
              </span>
              <span className="ml-5">{normalized.rationale}</span>
            </div>
          )}

          {normalized.distractorAnalysis.length > 0 && (
            <div className="regular-text mt-2">
              <span className="flex align-items-center gap-2 incorrect">
                <i className="pi pi-times-circle text-red-500 text-base"></i>
                {languageText?.wrongAnswerAnalysis}
              </span>
              <span>
                {normalized.distractorAnalysis.map((d) => (
                  <div key={d.id} style={{ marginTop: 4 }} className="ml-5">
                    <strong>{d.id}.</strong> {d.text}
                  </div>
                ))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TopikInteractiveCard;

